import { ConnectorConfigModal } from "@/features/Data/components/ConnectorConfigModal/ConnectorConfigModal";
import { integrationData } from "@/features/Data/constants";
import { usePublicConnectors } from "@/features/Data/hooks/usePublicConnectors";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { appendConnectorToPromptbook } from "@/features/Promptbooks/actions/appendConnectorToPromptbook";
import { createResource } from "@/features/Resources/actions/createResource";
import { updateUserSettings } from "@/features/User/actions/updateUserSettings";
import { getFirstWorkspace } from "@/features/Workspaces/actions/getFirstWorkspace";
import { Box, Flex, FormControl, FormLabel, Spinner, Stack, chakra, useDisclosure, useToast } from "@chakra-ui/react";
import { ConnectorType } from "@pufflig/prompt-studio-types";
import { useState } from "react";
import { ListItemCard } from "./ListItemCard";

interface DataSetupStepProps {
  onSubmit: () => void;
}

export const DataSetupStep: React.FC<DataSetupStepProps> = (props) => {
  const { onSubmit } = props;
  const [selectedConnectorType, setSelectedConnectorType] = useState<ConnectorType | null>(null);
  const { data: publicConnectors, isLoading } = usePublicConnectors();
  const toast = useToast();
  const { navigateToPromptbook } = useAppNavigation();

  const {
    isOpen: isConnectorConfigOpen,
    onOpen: _onOpenConnectorConfigModal,
    onClose: onCloseConnectorConfig,
  } = useDisclosure();

  const onOpenConnectorConfigModal = (connectorType: ConnectorType) => {
    setSelectedConnectorType(connectorType);
    _onOpenConnectorConfigModal();
  };

  const onCloseConnectorConfigModal = (connectorId?: string) => {
    onCloseConnectorConfig();
    if (connectorId) {
      handleSubmit({ connectorId });
    }
  };

  const handleSubmit = async (props: any) => {
    try {
      const workspaceId = await getFirstWorkspace();

      if (!workspaceId) {
        throw new Error("No workspace found");
      }

      const { resourceId } = await createResource("My Promptbook", workspaceId, "promptbook");

      if (!resourceId) {
        throw new Error("Failed to create promptbook");
      }

      const connectorId = props.connectorId;

      await updateUserSettings({
        isDataSetupComplete: true,
      });

      if (connectorId) {
        await appendConnectorToPromptbook(connectorId, resourceId);
        navigateToPromptbook(resourceId);
      }

      onSubmit();
    } catch (error) {
      toast({
        position: "top",
        title: "Error",
        description: "Failed to save profile information. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Stack spacing="spacing-xl">
        {/* <FormControl as={Flex} gap="spacing-xs" direction={"column"}>
          <FormLabel mb="0">Upload a file</FormLabel>
          <Box position="relative" w="100%">
            <Input
              type="file"
              accept=".csv"
              id="csv-upload"
              display="none"
              onChange={(e) => setUploadedFile(e.target.files?.[0] ?? null)}
            />
            <FormLabel htmlFor="csv-upload" margin={0}>
              <ListItemCard
                title={uploadedFile?.name || "Upload .csv file"}
                rightIcon="add"
                logo={<Icon name="document-1" color="foreground.primary" />}
              />
            </FormLabel>
          </Box>
        </FormControl> */}
        <FormControl as={Flex} gap="spacing-xs" direction={"column"}>
          <FormLabel mb="0">Connect your database</FormLabel>
          <Box>
            <Stack spacing="0">
              {Object.entries(integrationData)
                .filter(([, { isAvailable }]) => isAvailable)
                .map(([type, { title, logo }], index, arr) => {
                  const isLast = index === arr.length - 1;
                  return (
                    <>
                      <ListItemCard
                        key={type}
                        onClick={() => onOpenConnectorConfigModal(type)}
                        rightIcon="add"
                        title={title}
                        logo={logo}
                      />
                      {!isLast && <chakra.hr my="spacing-xs" />}
                    </>
                  );
                })}
            </Stack>
          </Box>
        </FormControl>
        <FormControl as={Flex} gap={"spacing-xs"} direction={"column"}>
          <FormLabel mb="0">Get started with demo data</FormLabel>
          <Box maxHeight="200px" overflowY="auto">
            <Flex direction="column">
              {isLoading ? (
                <Box display="flex" justifyContent="center" padding="spacing-md">
                  <Spinner size="sm" />
                </Box>
              ) : (
                <>
                  {(publicConnectors ?? []).map(({ name, description, resource_id }) => {
                    return (
                      <ListItemCard
                        onClick={() => handleSubmit({ connectorId: resource_id })}
                        title={name}
                        description={description}
                        rightIcon="arrow-right-1"
                      />
                    );
                  })}
                </>
              )}
            </Flex>
          </Box>
        </FormControl>
      </Stack>
      <ConnectorConfigModal
        selectedConnectorType={selectedConnectorType}
        isOpen={isConnectorConfigOpen}
        onClose={onCloseConnectorConfigModal}
        teamId={undefined}
      />
    </>
  );
};
